/**
 * Dira Chatbot
 * --------------------------------------------------------------------------
 * Turns any element carrying the `.js-dira-trigger` class into an opener for
 * a single, reusable Dira chatbot popup.
 *
 * - The chatbot URL and enabled flag are read from <body data-dira-url / data-dira-enabled>,
 *   which are populated from the theme settings (settings.dira_chatbot_url / _enabled).
 *   The URL is NEVER hardcoded here — settings_schema.json is the single source of truth.
 * - One popup + one iframe is built and reused for every trigger (no per-click duplication).
 * - The iframe src is set lazily on first open so the chatbot is not loaded on page load.
 * - Click handling uses event delegation on `document`, so triggers rendered later
 *   (e.g. by sections loaded via AJAX or the theme editor) work without re-binding.
 *
 * Open/close model:
 *   A trigger ALWAYS opens the popup (re-opening when already open is a no-op). The popup
 *   is closed only via the ✕ button, the Escape key, or a click outside the panel. With
 *   several triggers on one page (e.g. PDP main CTA + sticky cart), a toggle would make a
 *   second trigger close the popup, which reads as broken — so triggers are open-only.
 *
 * Design tradeoff — close behaviour:
 *   On close we HIDE the popup rather than destroy the iframe. This preserves the
 *   chatbot conversation/session across open→close→reopen (re-creating the iframe
 *   would reload the app and drop any in-progress chat). The cost is that the iframe
 *   stays in the DOM holding the remote session for the rest of the page life, which
 *   is an acceptable trade for a chat widget where session continuity matters most.
 */
(function () {
  'use strict';

  var STYLE_ID = 'dira-chatbot-styles';
  var LOCK_CLASS = 'dira-chatbot-lock';
  var TRIGGER_SELECTOR = '.js-dira-trigger';

  function init() {
    var body = document.body;
    if (!body) return;

    // Treat anything other than an explicit "false" as enabled (covers true / missing / "").
    var enabledAttr = body.getAttribute('data-dira-enabled');
    if (enabledAttr === 'false') return;

    var diraUrl = (body.getAttribute('data-dira-url') || '').trim();
    if (!diraUrl) return; // No URL configured → nothing to open.

    injectStyles();

    var widget = buildWidget();
    var iframeLoaded = false;
    var lastTrigger = null;

    function isOpen() {
      return widget.root.classList.contains('dira-chatbot--open');
    }

    function openWidget() {
      if (isOpen()) return; // Open-only: re-opening is a no-op.

      // Lazy-load the iframe on first open only.
      if (!iframeLoaded) {
        widget.iframe.src = diraUrl;
        iframeLoaded = true;
      }
      widget.root.classList.add('dira-chatbot--open');
      widget.root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(LOCK_CLASS); // Lock page scroll behind full-screen mobile panel.
      widget.closeBtn.focus();
    }

    function closeWidget() {
      if (!isOpen()) return;
      widget.root.classList.remove('dira-chatbot--open');
      widget.root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(LOCK_CLASS);
      // Return focus to the trigger that opened the popup, for keyboard/screen-reader users.
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus();
      }
    }

    // Event delegation: works for current and dynamically-added triggers.
    document.addEventListener('click', function (event) {
      var target = event.target;
      // Guard: some click targets (e.g. non-element nodes) have no .closest — bail safely
      // so a stray click never throws and breaks every click handler on the page.
      if (!target || typeof target.closest !== 'function') return;

      var trigger = target.closest(TRIGGER_SELECTOR);
      if (trigger) {
        event.preventDefault();
        lastTrigger = trigger;
        openWidget();
        return;
      }

      // Click outside the panel closes it (the ✕ lives inside .dira-chatbot, so its own
      // handler deals with it and this branch is skipped for it).
      if (isOpen() && !target.closest('.dira-chatbot')) {
        closeWidget();
      }
    });

    // Close button.
    widget.closeBtn.addEventListener('click', closeWidget);

    // Escape key closes the popup when open.
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) {
        closeWidget();
      }
    });
  }

  /**
   * Build the popup DOM once and append it to <body>.
   * Returns references used by the open/close logic.
   */
  function buildWidget() {
    var root = document.createElement('div');
    root.className = 'dira-chatbot';
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Ask Dira');

    var header = document.createElement('div');
    header.className = 'dira-chatbot__header';

    var title = document.createElement('span');
    // title.className = 'dira-chatbot__title';
    // title.textContent = 'Ask Dira';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'dira-chatbot__close';
    closeBtn.setAttribute('aria-label', 'Close Dira chat');
    closeBtn.innerHTML = '&#10005;'; // ✕

    header.appendChild(title);
    header.appendChild(closeBtn);

    var iframe = document.createElement('iframe');
    iframe.className = 'dira-chatbot__iframe';
    iframe.setAttribute('title', 'Dira chatbot');
    iframe.setAttribute('allow', 'clipboard-write; microphone');
    // src is intentionally left unset here — assigned on first open (lazy load).

    root.appendChild(header);
    root.appendChild(iframe);
    document.body.appendChild(root);

    return { root: root, closeBtn: closeBtn, iframe: iframe };
  }

  /**
   * Inject scoped styles once. All selectors are prefixed `dira-chatbot-` /
   * `.dira-chatbot` to avoid clashing with theme CSS.
   */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var css = [
      '.dira-chatbot{',
      '  position:fixed;bottom:20px;right:20px;z-index:2147483000;',
      '  width:380px;height:600px;max-height:calc(100vh - 40px);',
      '  display:none;flex-direction:column;',
      '  background:#fff;border-radius:14px;',
      '  box-shadow:0 18px 48px -12px rgba(0,0,0,0.45);',
      '  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
      // Firefox — matches the thin, brand-gold scrollbar used across the theme (see component-facets.css).
      '  scrollbar-width:thin;scrollbar-color:var(--s-brand,#bf9b30) transparent;',
      '}',
      '.dira-chatbot::-webkit-scrollbar{width:4px;}',
      '.dira-chatbot::-webkit-scrollbar-track{background:transparent;}',
      '.dira-chatbot::-webkit-scrollbar-thumb{background-color:var(--s-brand,#bf9b30);border-radius:4px;}',
      '.dira-chatbot::-webkit-scrollbar-thumb:hover{background-color:var(--s-muted,#8a7a63);}',
      '.dira-chatbot.dira-chatbot--open{display:flex;}',
      '.dira-chatbot__header{',
      '  display:flex;align-items:center;justify-content:space-between;',
      '  padding:0px;background:#bf9b30;color:var(--s-night, #2a1b0e);flex:0 0 auto;',
      '}',
      '.dira-chatbot__title{font-size:15px;font-weight:600;letter-spacing:0.01em;}',
      '.dira-chatbot__close{',
      '  appearance:none;-webkit-appearance:none;border:none;background:rgb(191 155 48/var(--tw-bg-opacity,1));',
      '  color:inherit;font-size:16px;line-height:1;cursor:pointer;padding:6px 8px;',
      '  border-radius:16px;transition:background 0.15s ease,opacity 0.15s ease;opacity:0.85; position:absolute; right:-12px;',
      '}',
      '.dira-chatbot__close:hover{opacity:1;background:rgba(255,255,255,0.12);}',
      '.dira-chatbot__iframe{flex:1 1 auto;width:100%;border:0;display:block; border-radius:12px 12px 0 0;}',
      '@media (max-width:480px){',
      '  .dira-chatbot{',
      '    top:60px;right:0;bottom:0;left:0;width:100%;height:calc(100% - 60px);',
      '    max-height:100%;border-radius:0;',
      '  }',
      '.dira-chatbot__close{right: 6px;top: 18px;background:transparent !important; color:#fff; font-size:16px;}',
       '.dira-chatbot__iframe{border-radius:0;}',
      // Lock page scroll only while the full-screen mobile panel is open.
      '  html.' + LOCK_CLASS + ',html.' + LOCK_CLASS + ' body{overflow:hidden;}',
      '}',
      '@media (prefers-reduced-motion:reduce){',
      '  .dira-chatbot__close{transition:none;}',
      '}'
    ].join('');

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
